import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, Mail, ArrowLeft, ChevronLeft, ChevronRight, Brain, Heart, GraduationCap, Car, Home, Truck, UtensilsCrossed, Blocks, Music } from 'lucide-react';

export const Sidebar = ({ onBack, isCollapsed, onToggleCollapse, activeFilter, onFilterChange, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const navItems = [
    { id: 'all', label: 'Our AI Companies', icon: Building2, filter: 'all' },
    { id: 'ai', label: 'AI Solutions', icon: Brain, filter: 'ai' },
    { id: 'healthcare', label: 'Healthcare', icon: Heart, filter: 'healthcare' },
    { id: 'education', label: 'Education', icon: GraduationCap, filter: 'education' },
    { id: 'realestate', label: 'Real Estate', icon: Home, filter: 'realestate' },
    { id: 'logistics', label: 'Logistics', icon: Truck, filter: 'logistics' },
    { id: 'hospitality', label: 'Hospitality', icon: UtensilsCrossed, filter: 'hospitality' },
    { id: 'entertainment', label: 'Entertainment', icon: Music, filter: 'entertainment' },
    { id: 'blockchain', label: 'Blockchain', icon: Blocks, filter: 'blockchain' },
    { id: 'automotive', label: 'Automotive', icon: Car, filter: 'automotive' },
  ];

  return (
    <motion.aside
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1, width: isMobile ? 280 : (isCollapsed ? 80 : 288) }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed left-0 top-0 h-screen glass flex flex-col justify-between p-4 z-50 ${isMobile ? 'w-[280px]' : ''}`}
      data-testid="sidebar"
    >
      {/* Collapse Toggle Button - Hidden on mobile */}
      {!isMobile && (
        <button
          onClick={onToggleCollapse}
          className="absolute -right-3 top-8 w-6 h-6 bg-zinc-800 border border-white/10 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors z-50 hidden lg:flex"
          data-testid="sidebar-toggle"
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      )}

      {/* Top Section */}
      <div className={isCollapsed && !isMobile ? 'px-2' : 'px-4'}>
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-8 mt-2 lg:mt-0"
        >
          {(!isCollapsed || isMobile) && (
            <button
              onClick={onBack}
              className="group flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-6"
              data-testid="back-to-hero"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-xs font-mono uppercase tracking-wider">Back</span>
            </button>
          )}
          {isCollapsed && !isMobile ? (
            <button
              onClick={onBack}
              className="w-full flex justify-center text-zinc-400 hover:text-white transition-colors"
              data-testid="back-to-hero-collapsed"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <>
              <h2 className="text-2xl font-bold tracking-tight text-white">
                Waterline
              </h2>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">
                Group
              </p>
            </>
          )}
        </motion.div>

        {/* Navigation */}
        <nav className="space-y-1">
          {(!isCollapsed || isMobile) && (
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-600 mb-3 pl-4">
              Industries
            </p>
          )}
          {navItems.map((item, index) => (
            <motion.button
              key={item.id}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 + index * 0.05, duration: 0.5 }}
              onClick={() => onFilterChange(item.filter)}
              className={`nav-link w-full flex items-center ${isCollapsed && !isMobile ? 'justify-center' : 'gap-3'} px-4 py-2.5 rounded-lg text-left transition-all
                ${activeFilter === item.filter 
                  ? 'text-white bg-white/10 active' 
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              data-testid={`nav-${item.id}`}
              title={isCollapsed && !isMobile ? item.label : ''}
            >
              <item.icon className="w-5 h-5" />
              {(!isCollapsed || isMobile) && <span className="font-medium text-sm">{item.label}</span>}
            </motion.button>
          ))}
        </nav>
      </div>

      {/* Bottom Section */}
      {(!isCollapsed || isMobile) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="space-y-6 px-4"
        >
          {/* Contact Link */}
          <a
            href="mailto:info@waterlinegroup.com"
            className="flex items-center gap-2 text-zinc-500 hover:text-sky-400 transition-colors text-sm"
            data-testid="contact-link"
          >
            <Mail className="w-4 h-4" />
            <span>info@waterlinegroup.com</span>
          </a>
          
          {/* Copyright */}
          <div className="pt-6 border-t border-white/5">
            <p className="text-xs text-zinc-600">
              © 2024 Waterline Group
            </p>
            <p className="text-xs text-zinc-700 mt-1">
              San Diego, California
            </p>
          </div>
        </motion.div>
      )}

      {isCollapsed && !isMobile && (
        <div className="flex justify-center">
          <a
            href="mailto:info@waterlinegroup.com"
            className="text-zinc-500 hover:text-sky-400 transition-colors"
            data-testid="contact-link-collapsed"
            title="Contact"
          >
            <Mail className="w-5 h-5" />
          </a>
        </div>
      )}
    </motion.aside>
  );
};
